<template>
    <div class="vehicles">
        <div class="vehicles__content">
            <div v-if="displayContent && cmsPage && cmsPage.parent" class="vehicles__cms-page-header">
                <PageBreadcrumbs :page="cmsPage" />
            </div>

            <ServiceTrustIndicators v-if="showServiceTrustIndicators" />

            <div
                ref="searchBox"
                class="vehicles__search-box"
                :class="{ 'is-active': isMainFiltersActive, 'is-with-trust-indicators': showServiceTrustIndicators }"
            >
                <VehicleSearchForm
                    ref="searchForm"
                    :preview-mode="!isMainFiltersActive"
                    :loading="isLoadingVehicles"
                    :filters-open="isAdditionalFiltersOpen"
                    class="vehicles__search-form"
                    @toggle-filters="isAdditionalFiltersOpen = !isAdditionalFiltersOpen"
                    @turn-on-active-mode="activateMainFilters"
                />
            </div>

            <div ref="mapAndList" class="vehicles__map-and-list">
                <div
                    v-if="isMainFiltersActive"
                    class="vehicles__main-filters-overlay js-close-on-click"
                    @click="handleMainFilterOverlayClick($event)"
                />

                <div class="vehicles__results">
                    <div class="vehicles__results-wrapper">
                        <div class="vehicles__results__item">
                            <LoadingIndicator v-if="isLoading" />
                            <span v-else>{{ vehiclesCountFormatted }}</span>
                        </div>
                        <div class="vehicles__results__item vehicles__map-toggle">
                            <svg-icon name="map" />
                            <span>{{ $t('search_form.map') }}</span>
                            <Toggle v-model="isMapOpen" size="medium" />
                        </div>
                        <div class="vehicles__results__item">
                            <svg-icon name="sort" />
                            <span v-if="isLargeScreen">{{ $t('search_form.sort_label') }}</span>
                            <SortOptions :disabled="isLoading" />
                        </div>
                    </div>

                    <a v-if="isAnyFilterActive" @click="clearFiltersAndReload">
                        <svg-icon name="close" />{{ $t('search_form.remove_filters') }}
                    </a>
                </div>

                <div v-if="displayMap">
                    <LoadingIndicator v-if="isLoadingVehicles" class="vehicles__map-loading" />
                    <VehicleMap :vehicles="mapVehicles" class="vehicles__map" />
                </div>
                <div v-else class="vehicles__list-wrap">
                    <VehicleSearchBanner />

                    <VehicleList
                        v-if="isLoading && vehicles.length === 0"
                        class="vehicles__loading"
                        :loading-count="24"
                    />

                    <div v-if="isEmptyStateVisible" class="vehicles__empty-state">
                        <div class="empty-state__text">
                            {{ $t('pages.vehicles.no_vehicles_found') }}
                        </div>
                        <button
                            type="button"
                            class="button button--secondary-contrast empty-state__button"
                            @click="clearFiltersAndReload"
                        >
                            {{ $t('pages.vehicles.no_vehicles_restart_button') }}
                        </button>
                    </div>
                    <VehicleList
                        v-else
                        :vehicles="vehicles"
                        :preload="true"
                        :show-matched-dates="true"
                        :list-name="VEHICLE_LIST_ANALYTICS_NAMES.searchPage"
                    />

                    <div
                        v-if="pagination && pagination.currentPage < pagination.totalPages"
                        class="vehicles__load-more"
                    >
                        <button
                            v-if="!isLoadingVehicles"
                            type="button"
                            class="button button--secondary-contrast"
                            @click="fetchVehiclePage(pagination.currentPage + 1)"
                        >
                            {{ $t('pages.vehicles.show_more_button') }}
                        </button>
                        <LoadingIndicator v-if="isLoadingVehicles">
                            {{ $t('loading.fetching_more_vehicles') }}
                        </LoadingIndicator>
                    </div>

                    <div v-if="!isLoadingCmsPage && cmsPage" class="vehicles__cms-page-container">
                        <div class="section section__content cms-page-container__wrapper">
                            <div v-if="isHeadingVisible" class="vehicles__cms-intro">
                                <h1 class="vehicles__cms-heading">{{ cmsPage.heading }}</h1>

                                <div class="cms-content">
                                    <!-- eslint-disable-next-line vue/no-v-html We trust our own CMS pages -->
                                    <div v-if="cmsPage.excerpt" v-html="cmsPage.excerpt" />
                                    <template v-else-if="$te('pages.vehicles.page_excerpt_fallback')">
                                        <p>
                                            {{
                                                $t('pages.vehicles.page_excerpt_fallback', {
                                                    vehicleType: $tc(`common.${vehicleLabel}`, 1),
                                                })
                                            }}
                                        </p>
                                    </template>
                                </div>
                            </div>
                            <div class="cms-page-container__content cms-content">
                                <!-- eslint-disable-next-line vue/no-v-html We trust our own CMS pages -->
                                <div v-html="cmsPage.content" />

                                <template v-if="cmsPage.faq.length > 0">
                                    <h3>{{ $t('pages.faq.heading') }}</h3>
                                    <div class="cms-page-container__faq cms-faq">
                                        <div
                                            v-for="(faq, index) in cmsPage.faq"
                                            :key="'faq-' + index"
                                            itemscope
                                            itemprop="mainEntity"
                                            itemtype="https://schema.org/Question"
                                        >
                                            <div class="cms-faq__question" itemprop="name">
                                                {{ faq.question }}
                                            </div>
                                            <div
                                                class="cms-faq__answer"
                                                itemscope
                                                itemprop="acceptedAnswer"
                                                itemtype="https://schema.org/Answer"
                                            >
                                                <div itemprop="text">{{ faq.answer }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </div>

                            <template v-if="cmsPage.children && cmsPage.children.length > 0">
                                <div class="cities-list">
                                    <h2 class="section__subheading">
                                        {{ $t('pages.vehicles.location_links_heading') }}
                                    </h2>
                                    <ul class="cities-list__list">
                                        <li
                                            v-for="childPage in cmsPage.children"
                                            :key="childPage.id"
                                            class="cities-list__item"
                                        >
                                            <LocalizedLink
                                                class="button button--secondary-contrast"
                                                :to="`${childPage.path}`"
                                            >
                                                {{ childPage.breadcrumbTitle }}
                                            </LocalizedLink>
                                        </li>
                                    </ul>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import gtmTrackingMixin from '~/mixins/gtmTrackingMixin';
import { ASSET_SUB_TYPE_CARAVAN, ASSET_SUB_TYPE_CAMPERVAN, ASSET_SUB_TYPE_MOTORHOME } from '~/config/assetTypes';
import {
    anyFiltersActive,
    buildQuery,
    getDefaultFilterValue,
    getFilterValue,
    removeAllFilters,
} from '~/helpers/queryParser';
import { bookingMethodValues } from '~/config/filters';
import {
    AVAILABLE_BETWEEN,
    BOOKING_METHOD,
    CHILDREN_ALLOWED,
    FESTIVALS_ALLOWED,
    LOCATION,
    MAX_ALLOWED_DRIVING_LICENSE_CLASS,
    MAX_LENGTH,
    MAX_MAXIMUM_AUTHORIZED_MASS,
    MAX_VEHICLE_AGE,
    MIN_INCLUDED_DISTANCE_PER_NIGHT,
    MINIMUM_AGE,
    PETS_ALLOWED,
    PLACE_DESCRIPTION,
    PRICE_RANGE,
    RADIUS,
    RENTABLE_OUTSIDE_OF_SWITZERLAND,
    SEARCH,
    SITS_AT_LEAST,
    SLEEPS_AT_LEAST,
    SMOKING_ALLOWED,
    SORT,
    SUB_TYPE,
    TOP_OWNER,
    TOWBAR_SOCKET,
    TRANSMISSION,
    WINTER_TYRE_TYPE,
    WINTERIZED,
} from '~/config/filterKeys';
import { VEHICLE_LIST_ANALYTICS_NAMES } from '~/config/analytics';
import { actionTypes as filterActionTypes } from '~/store/filters';

import VehicleList from '~/components/vehicles/VehicleList.vue';
import VehicleMap from '~/components/vehicles/VehicleMap.vue';
import VehicleSearchForm from '~/components/vehicles/VehicleSearchForm.vue';
import LoadingIndicator from '~/components/LoadingIndicator.vue';
import PageBreadcrumbs from '~/components/PageBreadcrumbs.vue';
import Toggle from '~/components/form-elements/Toggle.vue';
import SortOptions from '~/components/vehicles/SortOptions.vue';
import VehicleSearchBanner from '~/components/vehicles/VehicleSearchBanner.vue';
import ServiceTrustIndicators from '~/components/ServiceTrustIndicators.vue';
import {
    getBathroomEquipmentFields,
    getCommonAreaEquipmentFields,
    getDrivingEquipmentFields,
    getKitchenEquipmentFields,
    getOutdoorEquipmentFields,
} from '~/config/vehicleFields';

export default {
    components: {
        SortOptions,
        Toggle,
        VehicleSearchBanner,
        VehicleList,
        VehicleMap,
        VehicleSearchForm,
        LoadingIndicator,
        PageBreadcrumbs,
        ServiceTrustIndicators,
    },
    mixins: [gtmTrackingMixin],
    data() {
        return {
            isInitialLoadDone: false,
            isInitialMapLoadDone: false,
            isFirstPageLoaded: false,
            isLoadingVehicles: false,
            isMainFiltersActive: false,
            isAdditionalFiltersOpen: false,
            isMapOpen: false,
            vehicles: [],
            mapVehicles: [],
            pagination: null,
            vehiclesCount: null,
            displayContent: true,
            initialMainFiltersOffset: 0,
            locationValueChanged: false,
            availableBetweenValueChanged: false,

            VEHICLE_LIST_ANALYTICS_NAMES,
        };
    },
    async fetch() {
        // Store not updated at this point so we manually get 'siteCountryCode'
        const locale = new Intl.Locale(this._i18n.localeProperties.iso);
        const countryCode = locale.region || null;

        await this.updateVehicles(countryCode);
    },
    computed: {
        ...mapGetters([
            'cmsPage',
            'isLoadingCmsPage',
            'siteCountryCode',
            'favoredAsset',
            'user',
            'isLargeScreen',
            'standByFilters',
        ]),
        showServiceTrustIndicators() {
            return !this.isAllVehiclesRoute;
        },
        vehiclesCountFormatted() {
            let count = this.vehiclesCount;

            if (this.vehiclesCount > 1000) {
                count = `1000 +`;
            }

            return this.isLargeScreen
                ? this.$tc('pages.vehicles.num_of_results', this.vehiclesCount, { count })
                : count;
        },
        isAnyFilterActive() {
            return anyFiltersActive(this.$route.query);
        },
        selectedDates() {
            return getFilterValue(this.$route.query, AVAILABLE_BETWEEN);
        },
        activeVehicleSorting() {
            return getFilterValue(this.$route.query, SORT);
        },
        vehicleLabel() {
            const vehicleTypes = this.cmsPage?.searchSettings?.vehicleTypes;

            if (!vehicleTypes || vehicleTypes.length === 0) {
                return ASSET_SUB_TYPE_MOTORHOME;
            }

            if (vehicleTypes.includes(ASSET_SUB_TYPE_CAMPERVAN)) {
                return ASSET_SUB_TYPE_CAMPERVAN;
            }

            if (vehicleTypes.includes(ASSET_SUB_TYPE_MOTORHOME)) {
                return ASSET_SUB_TYPE_MOTORHOME;
            }

            return ASSET_SUB_TYPE_CARAVAN;
        },
        isAllVehiclesRoute() {
            return typeof this.$route.query[this.$i18n.t('common.all_vehicles_query')] !== 'undefined';
        },
        isLoading() {
            return this.isLoadingVehicles || this.isLoadingCmsPage;
        },
        displayMap() {
            return this.isMapOpen && this.isLargeScreen;
        },
        isEmptyStateVisible() {
            return this.isFirstPageLoaded && !this.isLoading && this.vehicles.length === 0;
        },
        isHeadingVisible() {
            return (
                this.displayContent &&
                this.cmsPage &&
                this.cmsPage.heading &&
                !this.isEmptyStateVisible &&
                !this.isAllVehiclesRoute &&
                !this.isLoading
            );
        },
        newQuery() {
            const cmsPageFilters = this.$store.getters.cmsPageFilters(this.$route.query, this.isAllVehiclesRoute);

            return buildQuery(this.$route.query, cmsPageFilters, {
                [LOCATION]: this.cmsPage?.searchSettings?.location,
            });
        },
        isPreviousRouteHomepage() {
            return this.$nuxt.context.from && this.$nuxt.context.from.path === '/';
        },
    },
    watch: {
        '$route.query': {
            handler() {
                this.isInitialLoadDone = false;
                this.isInitialMapLoadDone = false;
                this.updateVehicles();
            },
            deep: true,
        },
        isMapOpen: {
            handler() {
                // e.g. map active -> apply new filters ($route.query watcher takes care of map vehicles) -> close map (we have to update list vehicles)
                // and other way around
                if (!this.isInitialMapLoadDone || !this.isInitialLoadDone) {
                    this.updateVehicles();
                }
            },
        },
    },
    mounted() {
        this.$root.$on('turn-on-preview-mode', this.deactivateMainFilters);

        this.initialMainFiltersOffset = this.getViewportOffset(this.$refs.searchForm.$el).top;
    },
    methods: {
        getViewportOffset(element) {
            const offset = { top: element.offsetTop, left: element.offsetLeft };
            let parent = element.offsetParent;

            while (parent) {
                offset.top += parent.offsetTop;
                offset.left += parent.offsetLeft;
                parent = parent.offsetParent;
            }

            return offset;
        },
        activateMainFilters() {
            if (!this.isMainFiltersActive) {
                this.toggleMainFilters();
            }
        },
        deactivateMainFilters() {
            if (this.isMainFiltersActive) {
                this.toggleMainFilters();
            }
        },
        toggleMainFilters() {
            const searchBoxOffsetBefore = this.$refs.searchBox.offsetHeight;

            this.isMainFiltersActive = !this.isMainFiltersActive;

            // Workaround to prevent layout shift when main filters are toggled
            // Quirk: If the main filters are active and the viewport is scrolled down, when the main filters are toggled off,
            // there will still be a layout shift of the map and list.
            this.$nextTick(() => {
                const searchBoxOffsetAfter = this.$refs.searchBox.offsetHeight;

                const isSticky = this.getViewportOffset(this.$refs.searchForm.$el).top > this.initialMainFiltersOffset;

                if (this.isMainFiltersActive && !isSticky) {
                    this.$refs.mapAndList.style.top = `-${searchBoxOffsetAfter - searchBoxOffsetBefore}px`;
                } else {
                    this.$refs.mapAndList.style.top = '0';
                }
            });
        },
        clearFiltersAndReload() {
            const query = removeAllFilters(this.$route.query);
            this.dispatchClearFiltersEvent();

            return this.$router.push({ query });
        },
        handleMainFilterOverlayClick($event) {
            if ($event.target.classList.contains('js-close-on-click')) {
                this.toggleMainFilters();

                this.$store.dispatch(filterActionTypes.CLEAR_MAIN_STAND_BY_FILTERS).catch(err => {
                    this.$sentry.captureException(err);
                });
            }
        },
        updateVehicles(countryCode = this.siteCountryCode) {
            this.pagination = null;
            this.isFirstPageLoaded = false;

            if (this.isMapOpen) {
                this.mapVehicles = [];

                return this.fetchVehiclesForMap(countryCode)
                    .then(() => {
                        return Promise.resolve();
                    })
                    .catch(() => {}); // Intentional no-op since we're rejecting if already loading vehicles;
            } else {
                this.vehicles = [];
                this.vehiclesCount = 0;

                return this.fetchVehiclePage(1, countryCode)
                    .then(() => {
                        return Promise.resolve();
                    })
                    .catch(() => {}); // Intentional no-op since we're rejecting if already loading vehicles
            }
        },
        fetchVehiclesForMap(countryCode = this.siteCountryCode) {
            if (this.isLoadingVehicles) {
                // Avoid making multiple requests
                return Promise.reject(new Error('Avoid duplicate'));
            }

            this.isLoadingVehicles = true;

            // Update the list of vehicles for the map
            return this.$vehicleService
                .getVehicles(this.newQuery, countryCode, 1, 999, 'owner', 'map')
                .then(data => {
                    this.mapVehicles = data.vehicles;
                    this.vehiclesCount = data.total;

                    if (data.total === 0 && this.isAnyFilterActive) {
                        this.dispatchNoResultsEvent(this.$route.query);
                    } else {
                        this.dispatchEventsAndClearFilters();
                    }
                })
                .finally(() => {
                    this.isLoadingVehicles = false;
                    this.isInitialMapLoadDone = true;
                });
        },
        fetchVehiclePage(page, countryCode = this.siteCountryCode) {
            this.isFirstPageLoaded = true;

            if (this.isLoadingVehicles) {
                // Avoid making multiple requests
                return Promise.reject(new Error('Avoid duplicate'));
            }

            this.isLoadingVehicles = true;

            // Update the list of vehicles
            return this.$vehicleService
                .getVehicles(this.newQuery, countryCode, page, undefined, 'owner')
                .then(data => {
                    this.vehiclesCount = data.total;

                    if (data.total === 0 && this.isAnyFilterActive) {
                        const query = this.$route.query;
                        this.dispatchNoResultsEvent(query);
                    } else {
                        this.vehicles = this.vehicles.concat(data.vehicles);
                        this.pagination = data.pagination;

                        this.dispatchEventsAndClearFilters();
                    }
                })
                .catch(err => {
                    this.$sentry.captureException(err);
                })
                .finally(() => {
                    this.isLoadingVehicles = false;
                    this.isInitialLoadDone = true;
                });
        },
        dispatchEventsAndClearFilters() {
            if (process.client) {
                const newFiltersKeys = Object.keys(this.standByFilters);

                if (newFiltersKeys.length > 0) {
                    this.dispatchEvents(this.$route.query, newFiltersKeys);
                    this.$store.dispatch(filterActionTypes.CLEAR_STAND_BY_FILTERS);
                    this.$store.dispatch(filterActionTypes.CLEAR_ADDITIONAL_STAND_BY_FILTERS);
                    this.$store.dispatch(filterActionTypes.CLEAR_MAIN_STAND_BY_FILTERS).catch(err => {
                        this.$sentry.captureException(err);
                    });
                }
            }
        },
        dispatchClearFiltersEvent() {
            if (this.$gtm.enabled()) {
                window.dataLayer?.push({
                    event: 'clear_filters',
                    label: 'filters_applied',
                    locale: this.$i18n.locale,
                });
            }
        },
        dispatchLocationFilterEvent(place, query) {
            if (this.$gtm.enabled()) {
                window.dataLayer?.push({
                    event: 'location_filter',
                    method: this.isPreviousRouteHomepage ? 'homepage' : 'searchresult',
                    action: this.locationValueChanged ? 'changed_filter' : 'initial_filter',
                    label: place,
                    label_two: getFilterValue(query, RADIUS),
                    label_three: this.vehiclesCount,
                    locale: this.$i18n.locale,
                });
            }

            this.locationValueChanged = true;
        },
        dispatchTravelDateFilterEvent(dates) {
            const start = this.$options.filters.dateFormat(dates.from, 'dd.MM.');
            const end = this.$options.filters.dateFormat(dates.to, 'dd.MM.yyyy');

            if (this.$gtm.enabled()) {
                window.dataLayer?.push({
                    event: 'traveldate_filter',
                    method: this.isPreviousRouteHomepage ? 'homepage' : 'searchresult',
                    action: this.availableBetweenValueChanged ? 'changed_filter' : 'initial_filter',
                    label: `${start}-${end}`,
                    label_three: this.vehiclesCount,
                    locale: this.$i18n.locale,
                });
            }

            this.availableBetweenValueChanged = true;
        },
        dispatchPriceRangeFilterEvent(price) {
            const [from, to, currency] = price;
            const formattedPriceRange = `${from}-${to} ${currency?.toUpperCase()}`;

            if (this.$gtm.enabled()) {
                window.dataLayer?.push({
                    event: 'priceRange_filter',
                    label: formattedPriceRange,
                    locale: this.$i18n.locale,
                });
            }
        },
        dispatchIncludedDistancePerNightFilterEvent(value) {
            if (this.$gtm.enabled()) {
                window.dataLayer?.push({
                    event: 'includedDistancePerNight_filter',
                    label: value && value !== -1 ? value.replace(',', '-') : value,
                    locale: this.$i18n.locale,
                });
            }
        },
        dispatchVehicleAgeFilterEvent(value) {
            if (this.$gtm.enabled()) {
                window.dataLayer?.push({
                    event: 'vehicleAge_filter',
                    label: value?.replace(',', '-'),
                    locale: this.$i18n.locale,
                });
            }
        },
        dispatchEquipmentFilterEvent(query, equipment, event) {
            const appliedFilters = [];

            equipment.forEach(eq => {
                if (getFilterValue(query, eq.field)) {
                    appliedFilters.push(eq.field);
                }
            });

            if (appliedFilters.length === 0) {
                return;
            }

            if (this.$gtm.enabled()) {
                window.dataLayer?.push({
                    event,
                    label: appliedFilters.join(','),
                    locale: this.$i18n.locale,
                });
            }
        },
        dispatchFilterEvent(query, filter, event) {
            if (this.$gtm.enabled()) {
                window.dataLayer?.push({
                    event,
                    label: getFilterValue(query, filter),
                    locale: this.$i18n.locale,
                });
            }
        },
        dispatchBooleanFilterEvent(value, event) {
            if (this.$gtm.enabled()) {
                window.dataLayer?.push({
                    event,
                    label: value ? 'Yes' : 'No',
                    locale: this.$i18n.locale,
                });
            }
        },
        dispatchEventWithoutLabel(event) {
            if (this.$gtm.enabled()) {
                window.dataLayer?.push({
                    event,
                    locale: this.$i18n.locale,
                });
            }
        },
        dispatchEvents(query, newFiltersKeys) {
            const subType = getFilterValue(query, SUB_TYPE);
            const kitchenEquipment = getKitchenEquipmentFields(subType);
            const bathroomEquipment = getBathroomEquipmentFields(subType);
            const drivingEquipment = getDrivingEquipmentFields(subType);
            const interiorEquipment = getCommonAreaEquipmentFields(subType);
            const outdoorEquipment = getOutdoorEquipmentFields(subType);
            const priceRangeValue = getFilterValue(query, PRICE_RANGE);
            const minIncludedDistancePerNight = getFilterValue(query, MIN_INCLUDED_DISTANCE_PER_NIGHT);
            const maxVehicleAge = getFilterValue(query, MAX_VEHICLE_AGE);
            const bookingMethod = getFilterValue(query, BOOKING_METHOD);
            const availableBetween = getFilterValue(query, AVAILABLE_BETWEEN);
            const location = getFilterValue(query, PLACE_DESCRIPTION);

            if (newFiltersKeys.some(key => kitchenEquipment.some(eq => eq.field === key))) {
                this.dispatchEquipmentFilterEvent(query, kitchenEquipment, 'equipment_kitchen_filter');
            }

            if (newFiltersKeys.some(key => bathroomEquipment.some(eq => eq.field === key))) {
                this.dispatchEquipmentFilterEvent(query, bathroomEquipment, 'equipment_bathroom_filter');
            }

            if (newFiltersKeys.some(key => drivingEquipment.some(eq => eq.field === key))) {
                this.dispatchEquipmentFilterEvent(query, drivingEquipment, 'equipment_cab_filter');
            }

            if (newFiltersKeys.some(key => interiorEquipment.some(eq => eq.field === key))) {
                this.dispatchEquipmentFilterEvent(query, interiorEquipment, 'equipment_interior_filter');
            }

            if (newFiltersKeys.some(key => outdoorEquipment.some(eq => eq.field === key))) {
                this.dispatchEquipmentFilterEvent(query, outdoorEquipment, 'equipment_exterior_filter');
            }

            if (newFiltersKeys.includes(LOCATION) && location !== getDefaultFilterValue(LOCATION)) {
                this.dispatchLocationFilterEvent(location, query);
            }

            if (
                newFiltersKeys.includes(AVAILABLE_BETWEEN) &&
                availableBetween !== getDefaultFilterValue(AVAILABLE_BETWEEN)
            ) {
                this.dispatchTravelDateFilterEvent(availableBetween);
            }

            if (newFiltersKeys.includes(BOOKING_METHOD) && bookingMethod === bookingMethodValues.INSTANT) {
                this.dispatchEventWithoutLabel('instant_filter');
            }

            if (newFiltersKeys.includes(PRICE_RANGE) && priceRangeValue !== getDefaultFilterValue(PRICE_RANGE)) {
                this.dispatchPriceRangeFilterEvent(priceRangeValue);
            }

            if (
                newFiltersKeys.includes(MIN_INCLUDED_DISTANCE_PER_NIGHT) &&
                minIncludedDistancePerNight !== getDefaultFilterValue(MIN_INCLUDED_DISTANCE_PER_NIGHT)
            ) {
                this.dispatchIncludedDistancePerNightFilterEvent(minIncludedDistancePerNight);
            }

            if (newFiltersKeys.includes(MAX_VEHICLE_AGE) && maxVehicleAge !== getDefaultFilterValue(MAX_VEHICLE_AGE)) {
                this.dispatchVehicleAgeFilterEvent(maxVehicleAge);
            }

            this.handleBooleanFilterEvents(query, newFiltersKeys);
            this.handleFilterBasicEvents(query, newFiltersKeys);
            this.handleFilterEventsWithoutLabel(query, newFiltersKeys);
        },
        handleBooleanFilterEvents(query, newFiltersKeys) {
            const booleanFilters = [SMOKING_ALLOWED, FESTIVALS_ALLOWED];

            booleanFilters.forEach(filter => {
                const value = getFilterValue(query, filter);

                if (newFiltersKeys.includes(filter) && value !== getDefaultFilterValue(filter)) {
                    this.dispatchBooleanFilterEvent(value, `${filter}_filter`);
                }
            });
        },
        handleFilterEventsWithoutLabel(query, newFiltersKeys) {
            const filtersWithNoLabel = [
                CHILDREN_ALLOWED,
                PETS_ALLOWED,
                WINTERIZED,
                TOP_OWNER,
                RENTABLE_OUTSIDE_OF_SWITZERLAND,
            ];

            filtersWithNoLabel.forEach(filter => {
                if (
                    newFiltersKeys.includes(filter) &&
                    getFilterValue(query, filter) !== getDefaultFilterValue(filter)
                ) {
                    this.dispatchEventWithoutLabel(`${filter}_filter`);
                }
            });
        },
        handleFilterBasicEvents(query, newFiltersKeys) {
            const filtersWithBasicEvent = [
                SUB_TYPE,
                SLEEPS_AT_LEAST,
                SITS_AT_LEAST,
                MINIMUM_AGE,
                MAX_ALLOWED_DRIVING_LICENSE_CLASS,
                TRANSMISSION,
                MAX_LENGTH,
                RADIUS,
                TOWBAR_SOCKET,
                MAX_MAXIMUM_AUTHORIZED_MASS,
                SEARCH,
                WINTER_TYRE_TYPE,
            ];

            filtersWithBasicEvent.forEach(filter => {
                if (
                    newFiltersKeys.includes(filter) &&
                    getFilterValue(query, filter) !== getDefaultFilterValue(filter)
                ) {
                    const filterKey = filter === TOWBAR_SOCKET ? 'equipment_towbarSocket_filter' : `${filter}_filter`;
                    this.dispatchFilterEvent(query, filter, filterKey);
                }
            });
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';

.vehicles {
    @include font-size(16px);

    .main-layout__content,
    &__content {
        position: relative;
    }

    &__cms-page-container {
        background: $white;
        padding-left: 20px;
        padding-right: 20px;

        .cms-page-container {
            &__wrapper {
                max-width: 680px;
            }

            &__content {
                line-height: 1.575em;
                margin-bottom: 0.9em;

                h1 {
                    @extend %heading-1;
                }
                strong {
                    font-weight: bold;
                }
                em {
                    font-style: italic;
                }
                li {
                    padding: 5px;
                    margin-left: 35px;
                }

                ul {
                    list-style-type: disc;
                }
                ol {
                    list-style-type: decimal;
                }
            }
        }

        .cms-faq {
            &__question {
                margin-top: 20px;
                font-weight: bold;
                font-style: italic;
            }
        }
    }

    .cities-list {
        margin-top: 50px;
        &__list {
            list-style-type: none;
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-start;
            margin-top: 10px;
        }

        &__item {
            margin-right: 10px;
            margin-bottom: 10px;
        }
    }

    &__search-box {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: sticky;
        z-index: $z-vehicle-search-box;
        top: 0;
        background: $white;
        box-shadow: 0 4px 3px 0 rgba(0, 0, 0, 0.1);
        padding: 25px 20px 20px;

        &.is-with-trust-indicators {
            padding-top: 16px;
        }

        &.is-active {
            z-index: $z-sticky-header;
            @include lg {
                z-index: $z-vehicle-search-box;
            }
        }
    }

    &__main-filters-overlay {
        @include lg {
            background-color: $modal-background;
            position: fixed;
            top: 0;
            bottom: 0;
            width: 100%;
            box-sizing: border-box;
            overflow: hidden;
            z-index: $z-vehicle-search-box - 1;
        }
    }

    &__cms-page-header {
        width: 100%;
        box-sizing: border-box;
        position: relative;
        background: $white;
        padding: 20px 20px 10px;
        z-index: $z-vehicle-search-box + 1;
    }

    &__map-and-list {
        position: relative;
    }

    &__list-wrap {
        position: relative;
        background: $cream-white-light;
    }

    &__results {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 20px 0;
        @include font-size(14px);

        @include sm {
            padding-top: 24px;
            @include font-size(16px);
        }

        .svg-icon {
            width: 20px;
            height: 20px;
            margin-right: 4px;

            @include lg {
                width: 24px;
                height: 24px;
                margin-right: 8px;
            }
        }

        &__item {
            .sort-options {
                height: 30px;
            }

            &:not(:last-child) {
                padding-right: 10px;
                margin-right: 10px;
                border-right: 1px solid $gray-dark;
                display: flex;
                align-items: center;

                @include lg {
                    padding-right: 24px;
                    margin-right: 24px;
                }
            }
        }

        .loading-indicator__dot {
            @include font-size(24px);
        }
    }

    &__results-wrapper {
        display: flex;
    }

    &__map-toggle {
        display: none !important;
        font-weight: bold;

        @include breakpoint($bp-search-form) {
            display: flex !important;
            align-items: center;
        }

        span {
            @include font-size(16px);
            margin-right: 8px;
        }

        .toggle {
            margin-right: 0;
        }
    }

    &__empty-state {
        max-width: 680px;
        min-height: 250px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        margin: 0 auto;
        padding: 0 20px;
        text-align: center;
        overflow: hidden;

        .empty-state__text {
            @extend %heading-3;
        }
        .empty-state__button {
            margin: 30px auto 0;
            display: block;
        }

        @include lg {
            min-height: 450px;
        }
    }

    &__cms-intro {
        margin-bottom: 40px;
    }

    &__cms-heading {
        @extend %heading-2;
        text-transform: none;
        margin-bottom: 10px;

        @include md {
            margin-bottom: 20px;
        }
    }

    .vehicle-list {
        padding: 10px 10px 50px;

        @include sm {
            justify-content: left;
        }

        // Change in grid from this point onwards
        @include breakpoint($bp-search-form) {
            padding: 10px 8px 50px;
        }

        .vehicle-list__item {
            display: inline-block;
            margin: 10px;
            z-index: 0;
            @include font-size(14px);
            width: calc(100% - 20px);

            @include breakpoint(560px) {
                width: calc(100% / 2 - 20px);
                @include font-size(12px);
            }

            @include breakpoint(770px) {
                width: calc(100% / 3 - 20px);
            }

            @include breakpoint($bp-search-form) {
                margin: 12px;
                width: calc(100% / 3 - 24px);
                @include font-size(12px);
            }

            @include breakpoint(1330px) {
                width: calc(100% / 4 - 24px);
                @include font-size(14px);
            }

            @include breakpoint(2000px) {
                width: calc(100% / 6 - 24px);
            }
        }
    }

    &__load-more {
        text-align: center;
        margin: 20px 0 40px;
    }

    &__map-loading {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 1;
        background: rgba(255, 255, 255, 0.5);
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    &__map {
        display: none;
    }

    @include lg {
        display: flex;

        &__content {
            width: 100%;
        }

        &__map {
            display: block;
            position: sticky;
            width: 100%;
            top: 0;
            height: calc(100vh - #{$main-header-md-height});
            right: 0;
        }
    }

    .service-trust-indicators {
        z-index: $z-vehicle-search-box;
    }
}

// Adjust the map height for when the main header banner is shown
.header-banner-active .vehicles__map {
    top: calc(#{$main-header-md-height} + #{$main-banner-height});
    height: calc(100vh - #{$main-header-md-height} - #{$main-banner-height});
}
</style>
